/** Angular Imports */
import { isPlatformServer } from '@angular/common';
import { inject, PLATFORM_ID } from '@angular/core';
import { CanMatchFn, Router } from '@angular/router';
import { map } from 'rxjs';

/** Local Imports */
import { AuthService } from '@core/services/auth/auth.service';

/**
 * Route guard that allows access only to authenticated users.
 *
 * @param route - The activated route snapshot
 * @param segments - The URL segments
 * @returns {boolean | Observable<boolean>} True if user is authenticated, otherwise redirects to login
 *
 * @remarks
 * This is a functional guard (Angular 15+) used with `canMatch`.
 * First checks sessionStorage for quick validation, then verifies with backend.
 * Redirects unauthenticated users to /login.
 */
export const authGuard: CanMatchFn = (route, segments) => {
    /**
     * Authentication service instance
     * @type {AuthService}
     */
    const authService: AuthService = inject(AuthService);

    /**
     * Router service for navigation
     * @type {Router}
     */
    const router: Router = inject(Router);

    /**
     * Platform ID to check if running on server
     */
    const platformId = inject(PLATFORM_ID);

    // On the server, we allow the match to proceed so SSR can render the layout.
    // The client-side execution of this guard will perform the actual validation.
    if (isPlatformServer(platformId)) {
        return false;
    }

    // Quick check: if sessionStorage has token, allow immediately
    const isAuthenticated = authService.checkAuthStatus();

    if (isAuthenticated) {
        if (sessionStorage.getItem('ntv_pending_verification')) {
            return router.createUrlTree(['/auth']);
        }
        return true;
    }

    // If no sessionStorage token, check backend once
    // This handles refresh/reopen scenarios where httpOnly cookie might still be valid
    return authService.verifyAuthWithBackend().pipe(
        map((isAuth) => {
            if (!isAuth) {
                const returnUrl = '/' + segments.map((s) => s.path).join('/');
                const queryParams = returnUrl !== '/' ? { returnUrl } : {};
                return router.createUrlTree(['/login'], { queryParams });
            }
            return true;
        }),
    );
};
